import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Phone, Mail, User, PoundSterling, Calendar as CalendarIcon,
  MessageSquare, Save, X, Lightbulb, MapPin, Globe, Smartphone,
  Clock, Plus, CheckCircle, XCircle, PhoneOff, Voicemail,
  Send, Copy, ExternalLink, MessageCircle, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'callback', label: 'Needs Callback' },
];

const PIPELINE_STAGES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const CALLBACK_OPTIONS = [
  { days: 0, label: 'Today' },
  { days: 3, label: '3 Days' },
  { days: 7, label: '7 Days' },
  { days: 14, label: '14 Days' },
  { days: 28, label: '28 Days' },
];

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Phone Call', icon: Phone },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'note', label: 'Note', icon: MessageSquare },
];

const CALL_OUTCOMES = [
  { value: 'answered', label: 'Answered', icon: CheckCircle, color: 'text-emerald-600' },
  { value: 'no_answer', label: 'No Answer', icon: XCircle, color: 'text-red-500' },
  { value: 'voicemail', label: 'Voicemail', icon: Voicemail, color: 'text-amber-500' },
  { value: 'busy', label: 'Busy', icon: PhoneOff, color: 'text-slate-500' },
];

// Message templates
const MESSAGE_TEMPLATES = [
  {
    id: 'intro',
    name: 'Introduction',
    message: "Hi {name}, this is {user} from {business}. I'm following up on your enquiry about our training courses. When's a good time to chat?"
  },
  {
    id: 'callback',
    name: 'Callback Follow-up',
    message: "Hi {name}, hope you're well! Just following up as discussed - {callback_reason}. Would you like to proceed with booking?"
  },
  {
    id: 'reminder',
    name: 'Gentle Reminder',
    message: "Hi {name}, just a quick reminder about our {business} courses. We have limited spots available. Let me know if you have any questions!"
  },
  {
    id: 'deposit',
    name: 'Deposit Request',
    message: "Hi {name}, great speaking with you! To secure your spot, we just need a £100 deposit. I can send you the payment link whenever you're ready."
  },
  {
    id: 'thankyou',
    name: 'Thank You',
    message: "Hi {name}, thanks for booking with {business}! Your training starts soon. If you have any questions before then, just reply here."
  }
];

const LeadModal = ({ isOpen, onClose, lead, campaignId, onUpdate }) => {
  const { user, selectedBusiness } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [isLoading, setIsLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'manual',
    status: 'new',
    pipeline_stage: 'new',
    callback_date: null,
    callback_reason: '',
    revenue: 450,
    notes: ''
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [showReasonPrompt, setShowReasonPrompt] = useState(false);
  
  // Activity form state
  const [newActivity, setNewActivity] = useState({
    type: 'call',
    description: '',
    outcome: ''
  });

  // Chat tab state
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [chatActivities, setChatActivities] = useState([]);

  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [leadTags, setLeadTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        source: lead.source || 'manual',
        status: lead.status || 'new',
        pipeline_stage: lead.pipeline_stage || 'new',
        callback_date: lead.callback_date ? new Date(lead.callback_date) : null,
        callback_reason: lead.callback_reason || '',
        revenue: lead.revenue || 450,
        notes: lead.notes || ''
      });
      fetchActivities();
      fetchTags();
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        source: 'manual',
        status: 'new',
        pipeline_stage: 'new',
        callback_date: null,
        callback_reason: '',
        revenue: 450,
        notes: ''
      });
      setActivities([]);
      setChatActivities([]);
      setLeadTags([]);
    }
    setShowReasonPrompt(false);
    setNewActivity({ type: 'call', description: '', outcome: '' });
    setCustomMessage('');
    setSelectedTemplate('');
  }, [lead, isOpen]);

  const fetchActivities = async () => {
    if (!lead) return;
    setLoadingActivities(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/leads/${lead.lead_id}/activities`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const allActivities = await response.json();
        setActivities(allActivities);
        // Filter chat activities (sms, whatsapp, email)
        setChatActivities(allActivities.filter(a => 
          ['sms', 'whatsapp', 'email'].includes(a.activity_type)
        ));
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchTags = async () => {
    if (!lead) return;
    setLoadingTags(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const businessParam = selectedBusiness ? `?business_id=${selectedBusiness.business_id}` : '';
      
      // Fetch all available tags for the business
      const [allTagsRes, leadTagsRes] = await Promise.all([
        fetch(`${API_URL}/api/tags${businessParam}`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/leads/${lead.lead_id}/tags`, { credentials: 'include', headers })
      ]);
      
      if (allTagsRes.ok) {
        setAllTags(await allTagsRes.json());
      }
      if (leadTagsRes.ok) {
        setLeadTags(await leadTagsRes.json());
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleToggleTag = async (tag) => {
    const isCurrentlyAssigned = leadTags.some(t => t.tag_id === tag.tag_id);
    let newTagIds;
    
    if (isCurrentlyAssigned) {
      // Remove tag
      newTagIds = leadTags.filter(t => t.tag_id !== tag.tag_id).map(t => t.tag_id);
    } else {
      // Add tag
      newTagIds = [...leadTags.map(t => t.tag_id), tag.tag_id];
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads/${lead.lead_id}/tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tag_ids: newTagIds })
      });
      
      if (response.ok) {
        toast.success(isCurrentlyAssigned ? 'Tag removed' : 'Tag added');
        fetchTags();
      } else {
        toast.error('Failed to update tags');
      }
    } catch (error) {
      toast.error('Failed to update tags');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (formData.callback_date && !formData.callback_reason.trim()) {
      setShowReasonPrompt(true);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const payload = {
        ...formData,
        callback_date: formData.callback_date 
          ? format(formData.callback_date, 'yyyy-MM-dd')
          : null
      };

      let response;
      if (lead) {
        response = await fetch(`${API_URL}/api/leads/${lead.lead_id}`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            status: payload.status,
            pipeline_stage: payload.pipeline_stage,
            callback_date: payload.callback_date,
            callback_reason: payload.callback_reason,
            revenue: payload.revenue,
            notes: payload.notes
          })
        });
      } else {
        response = await fetch(`${API_URL}/api/leads`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            campaign_id: campaignId,
            name: payload.name,
            email: payload.email || null,
            phone: payload.phone || null,
            source: payload.source,
            notes: payload.notes
          })
        });
      }

      if (response.ok) {
        toast.success(lead ? 'Lead updated' : 'Lead created');
        onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save lead');
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error('Failed to save lead');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogActivity = async (activityType = null, description = null) => {
    const type = activityType || newActivity.type;
    const desc = description || newActivity.description;
    
    if (!desc.trim()) {
      toast.error('Please add a description');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(`${API_URL}/api/leads/${lead.lead_id}/activities`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          lead_id: lead.lead_id,
          activity_type: type,
          description: desc,
          outcome: newActivity.outcome || null
        })
      });

      if (response.ok) {
        toast.success('Activity logged');
        setNewActivity({ type: 'call', description: '', outcome: '' });
        setCustomMessage('');
        fetchActivities();
      } else {
        toast.error('Failed to log activity');
      }
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error('Failed to log activity');
    }
  };

  const handleCallbackQuick = (days) => {
    const date = addDays(new Date(), days);
    setFormData({ ...formData, callback_date: date, status: 'callback' });
    setShowReasonPrompt(true);
    toast.info(`Callback set for ${format(date, 'dd MMM yyyy')} - Please add a reason!`);
  };

  // Get personalized message from template
  const getPersonalizedMessage = (templateId) => {
    const template = MESSAGE_TEMPLATES.find(t => t.id === templateId);
    if (!template) return '';
    
    let message = template.message;
    message = message.replace('{name}', formData.name?.split(' ')[0] || 'there');
    message = message.replace('{user}', user?.name?.split(' ')[0] || 'us');
    message = message.replace('{business}', selectedBusiness?.name || user?.business_name || 'our company');
    message = message.replace('{callback_reason}', formData.callback_reason || 'your interest');
    
    return message;
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setCustomMessage(getPersonalizedMessage(templateId));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Message copied!');
  };

  const openWhatsApp = (message = '') => {
    if (formData.phone) {
      const cleanPhone = formData.phone.replace(/\s+/g, '').replace(/^\+/, '');
      const encodedMessage = encodeURIComponent(message || customMessage);
      window.open(`https://wa.me/${cleanPhone}${encodedMessage ? `?text=${encodedMessage}` : ''}`, '_blank');
    }
  };

  const openSMS = (message = '') => {
    if (formData.phone) {
      const encodedMessage = encodeURIComponent(message || customMessage);
      // Different format for iOS vs Android
      window.open(`sms:${formData.phone}${encodedMessage ? `?body=${encodedMessage}` : ''}`, '_blank');
    }
  };

  const openEmail = (message = '') => {
    if (formData.email) {
      const subject = encodeURIComponent(`Following up - ${selectedBusiness?.name || 'Your Enquiry'}`);
      const body = encodeURIComponent(message || customMessage);
      window.open(`mailto:${formData.email}?subject=${subject}&body=${body}`, '_blank');
    }
  };

  const statusColors = {
    new: 'status-new',
    interested: 'status-interested',
    not_interested: 'status-not-interested',
    callback: 'status-callback',
    contacted: 'status-contacted',
    qualified: 'status-qualified',
    won: 'status-won',
    lost: 'status-lost'
  };

  const activityTypeIcons = {
    call: Phone,
    sms: MessageSquare,
    whatsapp: MessageCircle,
    email: Mail,
    note: MessageSquare,
    status_change: Clock,
    callback_set: CalendarIcon
  };

  const activityTypeColors = {
    call: 'bg-blue-100 text-blue-600',
    sms: 'bg-purple-100 text-purple-600',
    whatsapp: 'bg-emerald-100 text-emerald-600',
    email: 'bg-amber-100 text-amber-600',
    note: 'bg-slate-100 text-slate-600'
  };

  const userName = user?.name?.split(' ')[0] || 'User';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6 w-[95vw] sm:w-full" aria-describedby="lead-modal-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg flex-wrap">
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500" />
            <span className="truncate flex-1">{lead ? formData.name : 'Add New Lead'}</span>
            {lead && (
              <Badge className={`ml-2 ${statusColors[formData.status] || 'status-new'} text-xs flex-shrink-0`}>
                {formData.status?.replace('_', ' ')}
              </Badge>
            )}
          </DialogTitle>
          {/* Lead Tags Display */}
          {lead && leadTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {leadTags.map(tag => (
                <Badge 
                  key={tag.tag_id}
                  className="text-white text-xs"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          )}
          <p id="lead-modal-description" className="sr-only">
            {lead ? 'View and update lead information' : 'Create a new lead'}
          </p>
        </DialogHeader>

        {lead ? (
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-3 h-auto">
              <TabsTrigger value="details" className="py-2.5 text-xs sm:text-sm">Details</TabsTrigger>
              <TabsTrigger value="chat" className="gap-1 sm:gap-2 py-2.5 text-xs sm:text-sm">
                <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Chat</span>
              </TabsTrigger>
              <TabsTrigger value="activity" className="py-2.5 text-xs sm:text-sm">Activity ({activities.length})</TabsTrigger>
            </TabsList>

            {/* ============ CHAT TAB ============ */}
            <TabsContent value="chat" className="space-y-3 sm:space-y-4 py-3 sm:py-4">
              {/* Contact Info Bar */}
              <div className="flex items-center gap-3 sm:gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{formData.name}</p>
                  <p className="text-sm text-slate-500 truncate">{formData.phone || formData.email || 'No contact info'}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {formData.phone && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="min-h-[40px] min-w-[40px] p-0"
                      onClick={() => window.open(`tel:${formData.phone}`)}
                    >
                      <Phone className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Callback Reason Banner */}
              {formData.callback_reason && (
                <Alert className="bg-amber-50 border-amber-200">
                  <CalendarIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <AlertDescription className="text-amber-800 text-sm">
                    <strong>Context:</strong> {formData.callback_reason}
                  </AlertDescription>
                </Alert>
              )}

              {/* Message Templates */}
              <div>
                <Label className="text-sm font-medium">Quick Templates</Label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2">
                  {MESSAGE_TEMPLATES.map((template) => (
                    <Button
                      key={template.id}
                      variant={selectedTemplate === template.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleTemplateSelect(template.id)}
                      className={`text-xs sm:text-sm min-h-[36px] ${selectedTemplate === template.id ? "bg-cyan-500 hover:bg-cyan-600" : ""}`}
                    >
                      {template.name}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Message Composer */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Message</Label>
                <Textarea
                  placeholder="Type your message or select a template above..."
                  className="min-h-[80px] sm:min-h-[100px] text-sm"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                />
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(customMessage)}
                    disabled={!customMessage}
                    className="min-h-[36px]"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <span className="text-xs text-slate-500">{customMessage.length} characters</span>
                </div>
              </div>

              {/* Send Buttons */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <Button
                  onClick={() => openWhatsApp(customMessage)}
                  disabled={!formData.phone}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs sm:text-sm px-2 sm:px-4 h-11"
                >
                  <svg className="w-4 h-4 sm:mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="hidden sm:inline">WhatsApp</span>
                </Button>
                <Button
                  onClick={() => openSMS(customMessage)}
                  disabled={!formData.phone}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 text-xs sm:text-sm px-2 sm:px-4 h-11"
                >
                  <MessageSquare className="w-4 h-4 sm:mr-2 flex-shrink-0" />
                  <span className="hidden sm:inline">SMS</span>
                </Button>
                <Button
                  onClick={() => openEmail(customMessage)}
                  disabled={!formData.email}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs sm:text-sm px-2 sm:px-4 h-11"
                >
                  <Mail className="w-4 h-4 sm:mr-2 flex-shrink-0" />
                  <span className="hidden sm:inline">Email</span>
                </Button>
              </div>

              {/* Quick Log After Sending */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Log this conversation
                </h4>
                <p className="text-sm text-slate-500">
                  After sending your message, log what you sent:
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLogActivity('whatsapp', `Sent WhatsApp: "${customMessage.substring(0, 50)}${customMessage.length > 50 ? '...' : ''}"`)}
                    disabled={!customMessage}
                    className="flex-1"
                  >
                    Log WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLogActivity('sms', `Sent SMS: "${customMessage.substring(0, 50)}${customMessage.length > 50 ? '...' : ''}"`)}
                    disabled={!customMessage}
                    className="flex-1"
                  >
                    Log SMS
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLogActivity('email', `Sent Email: "${customMessage.substring(0, 50)}${customMessage.length > 50 ? '...' : ''}"`)}
                    disabled={!customMessage}
                    className="flex-1"
                  >
                    Log Email
                  </Button>
                </div>
              </div>

              {/* Recent Chat History */}
              {chatActivities.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Recent Messages</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {chatActivities.slice(0, 5).map((activity) => {
                      const Icon = activityTypeIcons[activity.activity_type] || MessageSquare;
                      return (
                        <div 
                          key={activity.activity_id}
                          className="flex gap-3 p-3 bg-white border border-slate-200 rounded-lg"
                        >
                          <div className={`p-2 rounded-lg h-fit ${activityTypeColors[activity.activity_type] || 'bg-slate-100'}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700">{activity.description}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                              <span>{activity.user_name}</span>
                              <span>•</span>
                              <span>
                                {new Date(activity.created_at).toLocaleString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ============ DETAILS TAB ============ */}
            <TabsContent value="details" className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {formData.phone && (
                    <div>
                      <span className="text-slate-500">Phone:</span>
                      <span className="ml-2 font-medium">{formData.phone}</span>
                    </div>
                  )}
                  {formData.email && (
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <span className="ml-2 font-medium">{formData.email}</span>
                    </div>
                  )}
                </div>

                {/* Quick Contact Buttons */}
                <div className="flex flex-wrap gap-2">
                  {formData.phone && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => window.open(`tel:${formData.phone}`)}>
                        <Phone className="w-4 h-4 mr-2" /> Call
                      </Button>
                      <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => openWhatsApp()}>
                        <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Tags Section */}
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <Label className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-cyan-500" />
                  Tags
                </Label>
                {loadingTags ? (
                  <div className="flex justify-center py-2">
                    <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : allTags.length === 0 ? (
                  <p className="text-sm text-slate-500">No tags available. Create tags in Admin &gt; Tags.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((tag) => {
                      const isAssigned = leadTags.some(t => t.tag_id === tag.tag_id);
                      return (
                        <button
                          key={tag.tag_id}
                          onClick={() => handleToggleTag(tag)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            isAssigned 
                              ? 'text-white ring-2 ring-offset-1' 
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          style={isAssigned ? { 
                            backgroundColor: tag.color,
                            ringColor: tag.color
                          } : {}}
                        >
                          {isAssigned && <CheckCircle className="w-3 h-3 inline mr-1" />}
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Status & Pipeline */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Pipeline Stage</Label>
                    <Select value={formData.pipeline_stage} onValueChange={(v) => setFormData({ ...formData, pipeline_stage: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PIPELINE_STAGES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {isSuperAdmin && (
                  <div>
                    <Label htmlFor="revenue">Revenue (£)</Label>
                    <div className="relative mt-1.5">
                      <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="revenue"
                        type="number"
                        className="pl-10"
                        value={formData.revenue}
                        onChange={(e) => setFormData({ ...formData, revenue: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                )}

                {/* Callback Scheduling */}
                <div>
                  <Label>Schedule Callback</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {CALLBACK_OPTIONS.map((opt) => (
                      <Button key={opt.days} variant="outline" size="sm" onClick={() => handleCallbackQuick(opt.days)}>
                        {opt.label}
                      </Button>
                    ))}
                    <Popover open={showCalendar} onOpenChange={setShowCalendar}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm"><CalendarIcon className="w-4 h-4 mr-1" /> Custom</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.callback_date}
                          onSelect={(date) => {
                            setFormData({ ...formData, callback_date: date, status: 'callback' });
                            setShowCalendar(false);
                            setShowReasonPrompt(true);
                          }}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {formData.callback_date && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className="status-callback">
                        <CalendarIcon className="w-3 h-3 mr-1" />
                        {format(formData.callback_date, 'dd MMM yyyy')}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => setFormData({ ...formData, callback_date: null, callback_reason: '' })} className="h-6 px-2">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Callback Reason */}
                {(formData.callback_date || showReasonPrompt) && (
                  <div className="space-y-2">
                    <Alert className="bg-amber-50 border-amber-200">
                      <Lightbulb className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 text-sm">
                        <strong>Hey {userName},</strong> add a note for the callback reason so you remember the context!
                      </AlertDescription>
                    </Alert>
                    <Textarea
                      placeholder="E.g., 'Waiting for payday', 'Needs to discuss with partner'"
                      className="min-h-[60px]"
                      value={formData.callback_reason}
                      onChange={(e) => setFormData({ ...formData, callback_reason: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  placeholder="Add notes..."
                  className="mt-1.5 min-h-[60px]"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              {/* Lead Info */}
              <div className="pt-4 border-t border-slate-100 grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Source:</span><span className="ml-2 font-medium capitalize">{lead.source}</span></div>
                <div><span className="text-slate-500">Created:</span><span className="ml-2 font-medium">{new Date(lead.created_at).toLocaleDateString('en-GB')}</span></div>
              </div>

              {/* Save Button */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
                <Button onClick={handleSave} className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white" disabled={isLoading}>
                  <Save className="w-4 h-4 mr-2" />{isLoading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </TabsContent>

            {/* ============ ACTIVITY TAB ============ */}
            <TabsContent value="activity" className="space-y-4 py-4">
              {/* Log New Activity */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-medium text-slate-900">Log Activity</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={newActivity.type} onValueChange={(v) => setNewActivity({ ...newActivity, type: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ACTIVITY_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  {newActivity.type === 'call' && (
                    <div>
                      <Label>Outcome</Label>
                      <Select value={newActivity.outcome} onValueChange={(v) => setNewActivity({ ...newActivity, outcome: v })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                        <SelectContent>
                          {CALL_OUTCOMES.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Textarea
                  placeholder="What happened?"
                  className="min-h-[60px]"
                  value={newActivity.description}
                  onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                />
                <Button onClick={() => handleLogActivity()} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white" disabled={!newActivity.description.trim()}>
                  <Plus className="w-4 h-4 mr-2" /> Log Activity
                </Button>
              </div>

              {/* Activity History */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Activity History</h4>
                {loadingActivities ? (
                  <div className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                ) : activities.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p>No activities logged yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {activities.map((activity) => {
                      const Icon = activityTypeIcons[activity.activity_type] || Clock;
                      const outcome = CALL_OUTCOMES.find(o => o.value === activity.outcome);
                      return (
                        <div key={activity.activity_id} className="flex gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                          <div className={`p-2 rounded-lg h-fit ${activityTypeColors[activity.activity_type] || 'bg-slate-100 text-slate-600'}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-slate-900 capitalize">{activity.activity_type}</span>
                              {outcome && <Badge variant="secondary" className={outcome.color}>{outcome.label}</Badge>}
                            </div>
                            <p className="text-sm text-slate-700">{activity.description}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                              <span>{activity.user_name}</span>
                              <span>•</span>
                              <span>{new Date(activity.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          /* New Lead Form */
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="name" placeholder="Lead name" className="pl-10" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="phone" placeholder="+44 7700 900000" className="pl-10" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="email" type="email" placeholder="email@example.com" className="pl-10" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" placeholder="Add notes..." className="mt-1.5 min-h-[60px]" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-3 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button onClick={handleSave} className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white" disabled={isLoading}>
                <Save className="w-4 h-4 mr-2" />{isLoading ? 'Saving...' : 'Create Lead'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadModal;
